import type { TmsInvoiceRowDocuments, TmsPodDocumentState } from '@numeo/types-main-web-app'
import { Badge } from '@/components/ds2'

type ChipType = 'success' | 'warning' | 'danger' | 'default'

const POD_CHIP: Record<TmsPodDocumentState, { label: string; type: ChipType; title: string }> = {
    accepted: { label: 'POD ✓', type: 'success', title: 'POD accepted' },
    // "On file" read as "all good" on stage; the file is there but nobody accepted it yet.
    on_file: { label: 'POD to review', type: 'warning', title: 'A POD is on file but not accepted yet: review it and accept it' },
    none: { label: 'POD missing', type: 'danger', title: 'No POD on file' },
}

const Chip = ({ label, type, title }: { label: string; type: ChipType; title: string }) => (
    <span title={title} aria-label={title}>
        <Badge variant="soft" type={type} size="sm" label={label} />
    </span>
)

interface DocumentChipsProps {
    documents?: TmsInvoiceRowDocuments
    /** False shows "No driver"; undefined says nothing about the driver. */
    driverAssigned?: boolean
}

/**
 * A load's documents as chips (rate con, the POD's state, BOL) and "No driver" when the load has none assigned. A missing rate con
 * or POD is red: invoicing needs both. A missing BOL is grey: invoicing never asks for one. Nothing for a load that carries no
 * documents (a TONU needs neither a POD nor a rate con).
 */
export const DocumentChips = ({ documents, driverAssigned }: DocumentChipsProps) => {
    if (!documents && driverAssigned !== false) return null
    return (
        <div className="flex flex-wrap items-center gap-1">
            {documents && (
                <>
                    <Chip
                        label={documents.rate_con ? 'RC ✓' : 'RC missing'}
                        type={documents.rate_con ? 'success' : 'danger'}
                        title={documents.rate_con ? 'Rate con on file' : 'No rate con on file'}
                    />
                    <Chip {...(POD_CHIP[documents.pod] ?? POD_CHIP.none)} />
                    <Chip
                        label={documents.bol ? 'BOL ✓' : 'No BOL'}
                        type={documents.bol ? 'success' : 'default'}
                        title={documents.bol ? 'BOL on file' : 'No BOL on file (invoicing does not need one)'}
                    />
                </>
            )}
            {driverAssigned === false && <Chip label="No driver" type="warning" title="No driver assigned" />}
        </div>
    )
}
